import { Copy, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { generateShowcaseAccessCode, normalizeShowcaseAccessCode } from "@/utils/showcaseAccess";

interface ShowcaseAccessCodeSettingsProps {
  enabled: boolean;
  code: string;
  showcaseUrl: string;
  organizationName: string;
  onEnabledChange: (enabled: boolean) => void;
  onCodeChange: (code: string) => void;
}

export const ShowcaseAccessCodeSettings = ({
  enabled,
  code,
  showcaseUrl,
  organizationName,
  onEnabledChange,
  onCodeChange,
}: ShowcaseAccessCodeSettingsProps) => {
  const normalizedCode = normalizeShowcaseAccessCode(code);

  const shareMessage = `You can view the ${organizationName} cameras here:\n${showcaseUrl}\n\nAccess code: ${normalizedCode}`;

  const copy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch {
      toast.error("Couldn't copy automatically — please select and copy manually.");
    }
  };

  const handleToggle = (checked: boolean) => {
    if (checked && !normalizedCode) {
      const generated = generateShowcaseAccessCode();
      onCodeChange(generated);
    }
    onEnabledChange(checked);
  };

  return (
    <div className="mt-6 pt-6 border-t space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label className="text-sm font-medium flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Require an access code
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            Visitors must enter a shared code before they can see your cameras. This keeps
            casual visitors out — it is not strong security.
          </p>
        </div>
        <Switch
          id="showcase-access-code-switch"
          checked={enabled}
          onCheckedChange={handleToggle}
        />
      </div>

      {enabled && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={normalizedCode}
              onChange={(e) => onCodeChange(normalizeShowcaseAccessCode(e.target.value))}
              placeholder="Access code"
              maxLength={24}
              className="font-mono tracking-widest uppercase sm:max-w-[220px]"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCodeChange(generateShowcaseAccessCode())}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate code
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!normalizedCode}
              onClick={() => copy(normalizedCode, "Access code copied")}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy code
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!normalizedCode || !showcaseUrl}
              onClick={() => copy(shareMessage, "Share message copied")}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy share message
            </Button>
          </div>
          {!normalizedCode && (
            <p className="text-sm text-destructive">
              Add a code before saving, otherwise visitors cannot get in.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Changing the code locks out anyone who is still using the old one. Visitors stay
            signed in on their device for 30 days.
          </p>
        </div>
      )}
    </div>
  );
};
